/**
 * Utilidades centralizadas de teléfono basadas en libphonenumber-js.
 *
 * REGLA NO-NEGOCIABLE del proyecto:
 *   - DB / APIs / comparaciones internas: SIEMPRE formato E.164 SIN `+`
 *     Ej: `595991123456`
 *   - UI / display al usuario: SIEMPRE formato nacional
 *     Ej: `0991 123-456`
 *
 * Default region: PY (Paraguay). Para otros países pasar el segundo argumento.
 *
 * Estos helpers se usan tanto en server (validación de inputs, normalización
 * para persistir) como en client (PhoneInput vendoreado).
 */

import {
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

export const DEFAULT_REGION: CountryCode = 'PY';

export interface ParsedPhone {
  valid: boolean;
  /** E.164 SIN el `+` inicial. Formato interno del sistema. Ej: `595991123456`. */
  e164: string | null;
  /** E.164 estándar CON `+`. Útil para construir JIDs de WhatsApp, dial links, etc. */
  e164WithPlus: string | null;
  /** Display nacional, listo para mostrar en UI. Ej: `0991 123-456`. */
  national: string | null;
  /** Display internacional. Ej: `+595 991 123-456`. */
  internationalDisplay: string | null;
  /** ISO country code (PY, AR, US, etc.) o null si no se pudo determinar. */
  country: CountryCode | null;
}

const EMPTY: ParsedPhone = {
  valid: false,
  e164: null,
  e164WithPlus: null,
  national: null,
  internationalDisplay: null,
  country: null,
};

/**
 * Parsea cualquier formato razonable y devuelve representaciones canónicas.
 * Acepta:
 *  - `0991123456` (nacional)
 *  - `0991 123-456` (nacional formateado)
 *  - `+595991123456` (E.164 con +)
 *  - `595991123456` (E.164 sin +)
 *  - con espacios, guiones, paréntesis, lo que sea.
 *
 * Si el input no es válido devuelve `{ valid: false, ... null }`.
 */
export function parsePhone(
  raw: string | null | undefined,
  defaultRegion: CountryCode = DEFAULT_REGION
): ParsedPhone {
  if (!raw) return EMPTY;
  const cleaned = String(raw).trim();
  if (!cleaned) return EMPTY;

  // libphonenumber-js maneja la mayoría de formatos directamente.
  // Si viene un string puramente numérico sin + y sin 0 prefix, hay ambigüedad:
  // - "595991123456" (12 dígitos): probablemente E.164 → forzar prefijo +
  // - "991123456" (9 dígitos): nacional sin el 0 → la región default lo resuelve
  // Estrategia: si empieza con el código de país conocido y es lo suficientemente
  // largo, le agregamos `+`. Si no, lo dejamos como está y libphonenumber decide.
  let candidate = cleaned;
  if (!candidate.startsWith('+')) {
    // ¿Es E.164 sin +? Heurística: si arranca con un código de país de la región
    // y es 11-15 dígitos, agregamos +.
    if (/^\d{11,15}$/.test(candidate)) {
      candidate = '+' + candidate;
    }
  }

  const phone = parsePhoneNumberFromString(candidate, defaultRegion);
  if (!phone || !phone.isValid()) return EMPTY;

  const e164WithPlus = phone.number; // siempre `+XXXXX`
  return {
    valid: true,
    e164: e164WithPlus.replace(/^\+/, ''),
    e164WithPlus,
    national: phone.formatNational(),
    internationalDisplay: phone.formatInternational(),
    country: phone.country ?? null,
  };
}

/**
 * Atajo: normaliza al formato interno E.164 sin `+`, o devuelve null si inválido.
 * Uso típico: antes de guardar en DB.
 */
export function toE164(
  raw: string | null | undefined,
  defaultRegion: CountryCode = DEFAULT_REGION
): string | null {
  return parsePhone(raw, defaultRegion).e164;
}

/**
 * Atajo: formato nacional para display. Si el input no parsea, devuelve el
 * input original (para no romper UI con strings raros legados).
 */
export function toNationalDisplay(
  raw: string | null | undefined,
  defaultRegion: CountryCode = DEFAULT_REGION
): string {
  if (!raw) return '';
  return parsePhone(raw, defaultRegion).national ?? String(raw);
}

/**
 * Atajo: ¿es válido como teléfono en la región dada?
 */
export function isValidPhone(
  raw: string | null | undefined,
  defaultRegion: CountryCode = DEFAULT_REGION
): boolean {
  return parsePhone(raw, defaultRegion).valid;
}

/**
 * Convierte E.164 (con o sin `+`) al JID de WhatsApp para Baileys.
 * Ej: `595991123456` → `595991123456@s.whatsapp.net`.
 */
export function e164ToWhatsappJid(e164OrRaw: string): string {
  const e164 = toE164(e164OrRaw);
  if (!e164) {
    // Fallback: limpiar dígitos y construir igual (mejor algo que nada en logs)
    const digits = String(e164OrRaw).replace(/\D/g, '');
    return `${digits}@s.whatsapp.net`;
  }
  return `${e164}@s.whatsapp.net`;
}

/**
 * Extrae el número E.164 (sin +) de un JID de WhatsApp.
 * Ej: `595991123456@s.whatsapp.net` → `595991123456`.
 */
export function whatsappJidToE164(jid: string): string | null {
  const match = /^(\d+)@/.exec(jid);
  if (!match) return null;
  return match[1] ?? null;
}
